import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetLightLeadersQuery } from '../../store/redux/reducers/usersSlice'
import { RequestProps, User } from '../../store/types'
import { AutocompleteFetch } from '../Autocomplete/AutocompleteFetch'

interface AutocompleteLeadersProps {
  selectedLeaders: User[]
  setSelectedLeaders: (leaders: User[]) => void
  currentUser: User | null
}

const AutocompleteLeaders: React.FC<AutocompleteLeadersProps> = ({
  selectedLeaders,
  setSelectedLeaders,
  currentUser
}) => {
  const [page, setPage] = useState(0)
  const [displayedLeaders, setDisplayedLeaders] = useState<Map<string, User>>(new Map())
  const [searchTerm, setSearchTerm] = useState<string>('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const leaderProps = useMemo(() => {
    const defaultProps: RequestProps = {
      page: page,
      size: 20,
      'query:firstName,lastName': searchTerm,
    }
    return defaultProps
  }, [page, searchTerm])

  const { data: leadersData } = useGetLightLeadersQuery(leaderProps, {
    skip: !currentUser,
  })

  useEffect(() => {
    if (leadersData) {
      setDisplayedLeaders(prev => {
        const newLeaders = leadersData.content.filter(user => !prev.has(user.id))
        const updatedMap = new Map(prev)
        newLeaders.forEach(user => updatedMap.set(user.id, user))
        return updatedMap
      })
    }
  }, [leadersData, page, searchTerm])

  useEffect(() => {
    if (leadersData?.content) {
      if (!leadersData.last) {
        setPage(prev => prev + 1)
      }
    }
  }, [leadersData])

  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      const newSearchTerm = event.target.value
      setSearchTerm(newSearchTerm)
      setPage(0)
      if (newSearchTerm === '') {
        setDisplayedLeaders(new Map())
      }
    }, 500)
  }, [])

  const options = useMemo(() => {
    const leadersList = Array.from(displayedLeaders.values())
    return currentUser && !displayedLeaders.has(currentUser.id)
      ? [...leadersList, currentUser]
      : leadersList
  }, [displayedLeaders, currentUser])

  return (
    <AutocompleteFetch<User>
      selectedItems={selectedLeaders}
      setSelectedItems={setSelectedLeaders}
      options={options}
      getLabel={(user) => `${user.firstName} ${user.lastName}`}
      getId={(user) => user.id}
      labelTranslationKey="components.notificationSidebar.missingHoursNotification.selectLeader"
      onSearchChange={handleSearchInputChange}
    />
  )
}

export default AutocompleteLeaders